import { yupResolver } from '@hookform/resolvers/yup'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  Avatar,
  Box,
  FormHelperText,
  IconButton,
  InputAdornment,
  Link,
  OutlinedInput,
  Typography
} from '@mui/material'
import { MButton, ProgressLoaders } from 'components'
import QuickLinks from 'components/layouts/AuthorizedLayout/component/QuickLinks'
import {
  MESSAGES,
  ROUTES_PATH,
  USER_TOKEN
} from 'config'
import { FormLabel } from 'containers/Contact/Components'
import { useAuth } from 'context'
import { useSnackbar } from 'notistack'
import type React from 'react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { APIs } from 'services'
import { useLocalStorage } from 'utils'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import * as yup from 'yup'
import { NotificationPreferences, PaperlessBilling } from './components'
import UpdateEmail from './components/UpdateEmail'


interface ShowPasswordType {
  current: boolean
  new: boolean
  confirmNew: boolean
}

const schema = yup.object({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup.string().required('New password is required')
    .notOneOf([yup.ref('currentPassword')], 'New password must be different from the current password'),
  passwordConfirm: yup.string().oneOf([yup.ref('newPassword')], 'Passwords must match')
}).required()

interface IResetPwdForm {
  currentPassword: string
  newPassword: string
  passwordConfirm: string
}

export const AccountPreferences: React.FC = () => {
  const { account, currentAccount, getAccount, handleLogOut, isAdmin, userEmail } =
    useAuth()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [authToken] = useLocalStorage(USER_TOKEN, '')
  const [showPassword, setShowPassword] = useState<ShowPasswordType>({
    current: false,
    new: false,
    confirmNew: false
  })

  const isMobile = useMobileBreakpoints()
  const [prevAccountId, setPrevAccountId] = useState<string>('')
  const [isPrefUpdated, setIsPrefUpdated] = useState<boolean>(false)
  const [step, setStep] = useState<number>(0)
  const [passwordFormData, setPasswordFormData] = useState<IResetPwdForm | null>(null)
  const [otpCode, setOtpCode] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const RESEND_SECONDS = 120
  const [resendCountdown, setResendCountdown] = useState(RESEND_SECONDS)

  useEffect(() => {
    if (step !== 1 || resendCountdown <= 0) return
    const timer = setTimeout(() => {
      setResendCountdown((c) => c - 1)
    }, 1000)
    return () => { clearTimeout(timer) }
  }, [step, resendCountdown])

  const resendCode = async (): Promise<void> => {
    try {
      const response = await APIs.requestPasswordChange({ AccessToken: authToken })
      if (response?.success) {
        setResendCountdown(RESEND_SECONDS)
        enqueueSnackbar('Verification code resent to your email', {
          variant: 'success',
          autoHideDuration: 3000
        })
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }, message?: string }
      enqueueSnackbar(err?.response?.data?.error ?? err?.message ?? 'Error resending code', {
        variant: 'error',
        autoHideDuration: 5000
      })
    }
  }
  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<IResetPwdForm>({
    mode: 'all',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      passwordConfirm: ''
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any
  })

  useEffect(() => {
    if (prevAccountId === currentAccount?.accountId || currentAccount?.accountId === account?.myAccount?.accountId) {
      return
    }
    if (currentAccount?.accountId !== null) {
      void getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId,
        admin: isAdmin,
        email: userEmail
      })
    }
    setPrevAccountId((currentAccount?.accountId ?? account?.myAccount?.accountId) ?? '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken, currentAccount])

  useEffect(() => {
    if (isPrefUpdated) {
      void getAccount({
        AccessToken: authToken,
        accountId: currentAccount?.accountId ?? '',
        admin: isAdmin,
        email: userEmail
      })
      setIsPrefUpdated(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrefUpdated])

  const onRequestChange = async (data: IResetPwdForm): Promise<void> => {
    if (isAdmin) {
      enqueueSnackbar(MESSAGES.ADMIN_ACCESS_ERROR, {
        variant: 'error',
        autoHideDuration: 3000
      })
      return
    }

    if (data.passwordConfirm !== data.newPassword) {
      enqueueSnackbar(MESSAGES.PASSWORD_MATCH_ERROR, {
        variant: 'error',
        autoHideDuration: 3000
      })
      return
    }

    setIsLoading(true)
    try {
      const response = await APIs.requestPasswordChange({ AccessToken: authToken })
      if (response?.success) {
        setPasswordFormData(data)
        setResendCountdown(RESEND_SECONDS)
        setStep(1)
        enqueueSnackbar('Verification code sent to your email', {
          variant: 'success',
          autoHideDuration: 3000
        })
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }, message?: string }
      enqueueSnackbar(err?.response?.data?.error ?? err?.message ?? 'Error requesting password change', {
        variant: 'error',
        autoHideDuration: 3000
      })
    }
    setIsLoading(false)
  }

  const onConfirmChange = async (): Promise<void> => {
    if (!otpCode || !passwordFormData) return
    setIsLoading(true)
    try {
      const response = await APIs.changePassword({
        code: otpCode,
        currentPassword: passwordFormData.currentPassword,
        newPassword: passwordFormData.newPassword,
        AccessToken: authToken
      })
      if (response?.success) {
        // Send password change confirmation email
        if (userEmail) { await APIs.passwordResetConfirm({ email: userEmail, AccessToken: authToken }).catch(() => {}) }
        enqueueSnackbar(MESSAGES.PASSWORD_UPDATED_SUCCESS, {
          variant: 'success',
          autoHideDuration: 3000
        })
        void handleLogout()
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }, message?: string }
      enqueueSnackbar(err?.response?.data?.error ?? err?.message ?? 'Error changing password', {
        variant: 'error',
        autoHideDuration: 3000
      })
    }
    setIsLoading(false)
  }

  const handleLogout = async (): Promise<void> => {
    await handleLogOut()
    navigate(ROUTES_PATH.SIGNIN)
  }


  return (
    <>
      {isMobile && <QuickLinks />}
      {account !== null
        ? <Box mt={3} p={3} bgcolor="#ffffff" borderRadius="4px">
          <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={3}>
            <Box display="flex" flexDirection="column" flex={1} alignContent="space-between" gap={2}>
              <PaperlessBilling setIsPrefUpdated={setIsPrefUpdated} />
              <UpdateEmail />
            </Box>
            <Box flex={1} border='1px solid #E5E5F6' borderRadius="4px" p={3}>
              <Box display="flex" alignItems="center" flexDirection={isMobile ? 'row' : 'column'} gap={2}>
                <Avatar sx={{ bgcolor: '#F3FBFD', width: isMobile ? 50 : 80, height: isMobile ? 50 : 80 }}>
                  <LockOutlinedIcon fontSize={isMobile ? 'small' : 'large'} sx={{ color: '#1513C3' }} />
                </Avatar>
                <Typography color="#010B0E" fontWeight={600} fontSize={isMobile ? 18 : 24}>
                  Change Password
                </Typography>
              </Box>
              {step === 0 && (
                <form method="post" onSubmit={handleSubmit(onRequestChange)}>
                  <Box display="flex" flexDirection="column" mt={4} gap={3}>
                    <Box>
                      <FormLabel>CURRENT PASSWORD</FormLabel>
                      <Controller
                        name="currentPassword"
                        control={control}
                        render={({ field: { onChange, value, name } }): JSX.Element => (
                          <OutlinedInput
                            fullWidth
                            size="small"
                            type={showPassword.current ? 'text' : 'password'}
                            onChange={onChange}
                            value={value}
                            name={name}
                            placeholder="Enter your current password"
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setShowPassword({
                                      ...showPassword,
                                      current: !showPassword.current
                                    })
                                  }}
                                  edge="end"
                                >
                                  {showPassword.current
                                    ? (
                                      <VisibilityOff sx={{ color: '#3532F4' }} />
                                    )
                                    : (
                                      <Visibility sx={{ color: '#3532F4' }} />
                                    )}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        )}
                      />
                    </Box>
                    <Box>
                      <FormLabel>NEW PASSWORD</FormLabel>
                      <Controller
                        name="newPassword"
                        control={control}
                        render={({ field: { onChange, value, name } }): JSX.Element => (
                          <OutlinedInput
                            fullWidth
                            size="small"
                            type={showPassword.new ? 'text' : 'password'}
                            name={name}
                            onChange={onChange}
                            value={value}
                            placeholder="Enter the new password"
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setShowPassword({
                                      ...showPassword,
                                      new: !showPassword.new
                                    })
                                  }}
                                  edge="end"
                                >
                                  {showPassword.new
                                    ? (
                                      <VisibilityOff sx={{ color: '#3532F4' }} />
                                    )
                                    : (
                                      <Visibility sx={{ color: '#3532F4' }} />
                                    )}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        )}
                      />
                      {errors.newPassword &&
                        <FormHelperText error>{errors.newPassword.message}</FormHelperText>}
                    </Box>
                    <Box>
                      <FormLabel>CONFIRM NEW PASSWORD</FormLabel>
                      <Controller
                        name="passwordConfirm"
                        control={control}
                        render={({ field: { onChange, value, name } }): JSX.Element => (
                          <OutlinedInput
                            fullWidth
                            size="small"
                            type={showPassword.confirmNew ? 'text' : 'password'}
                            onChange={onChange}
                            value={value}
                            name={name}
                            placeholder="Enter again the new password"
                            endAdornment={
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setShowPassword({
                                      ...showPassword,
                                      confirmNew: !showPassword.confirmNew
                                    })
                                  }}
                                  edge="end"
                                >
                                  {showPassword.confirmNew
                                    ? (
                                      <VisibilityOff sx={{ color: '#3532F4' }} />
                                    )
                                    : (
                                      <Visibility sx={{ color: '#3532F4' }} />
                                    )}
                                </IconButton>
                              </InputAdornment>
                            }
                          />
                        )}
                      />
                      {errors.passwordConfirm &&
                        <FormHelperText error>{errors.passwordConfirm.message}</FormHelperText>}
                    </Box>
                    <MButton
                      size="large"
                      variant="contained"
                      rounded="true"
                      type="submit"
                      disabled={!isValid || isLoading}
                      texttransform="none"
                      loading={isLoading}
                    >
                      Confirm New Password
                    </MButton>
                  </Box>
                </form>
              )}
              {step === 1 && (
                <Box display="flex" flexDirection="column" mt={4} gap={3}>
                  <Typography color="#666" fontSize={14} textAlign="center">
                    A verification code has been sent to your email.
                  </Typography>
                  <Box textAlign="center" bgcolor="#f5f5f5" p={1.5} borderRadius="4px">
                    {resendCountdown > 0
                      ? (
                        <Typography fontSize={12}>
                          Resend code in <strong>{resendCountdown}s</strong>
                        </Typography>
                      )
                      : (
                        <>
                          <Typography display="inline" fontSize={12} fontWeight={600}>
                            Didn&apos;t get a code?{' '}
                          </Typography>
                          <Typography display="inline" fontSize={12}>
                            Request a new code by
                            <Link sx={{ textDecoration: 'none', cursor: 'pointer' }} onClick={() => { void resendCode() }}>
                              {' '}clicking here
                            </Link>.
                          </Typography>
                        </>
                      )}
                  </Box>
                  <Box>
                    <FormLabel>VERIFICATION CODE</FormLabel>
                    <OutlinedInput
                      fullWidth
                      size="small"
                      type="text"
                      value={otpCode}
                      onChange={(e) => { setOtpCode(e.target.value) }}
                      placeholder="Enter 6-digit code"
                      inputProps={{ maxLength: 6 }}
                    />
                  </Box>
                  <MButton
                    size="large"
                    variant="contained"
                    rounded="true"
                    type="button"
                    disabled={!otpCode || otpCode.length < 6 || isLoading}
                    texttransform="none"
                    onClick={onConfirmChange}
                    loading={isLoading}
                  >
                    Confirm Password Change
                  </MButton>
                  <MButton
                    size="large"
                    variant="text"
                    rounded="true"
                    type="button"
                    texttransform="none"
                    onClick={() => { setStep(0); setOtpCode('') }}
                  >
                    Back
                  </MButton>
                </Box>
              )}
            </Box>
          </Box>

          <NotificationPreferences setIsPrefUpdated={setIsPrefUpdated} />

        </Box >
        : <Box mt={3}><ProgressLoaders height='100%' /></Box>}
    </>

  )
}
